// import { newsService } from "./news.service";
import { type SentimentParams, SentimentParamsOptions } from "./news.models";
import type { NewsDO } from "./news.data";
import { capitalized, formatDate } from "../core/formatters";
import { DateOptionSet } from "../core/models/date";
import { i18n } from '../internationalize/i18n.service'

import './news.style.scss'
import type { NewsService } from "./news.service";
import { LoaderComponent } from "../charts/core/components/loader/loader-component";

export class NewsComponent extends HTMLElement {
    private news: NewsDO[] = []
    private keywords?: string | null
    private minScore?: string | null
    private table: HTMLTableElement | undefined
    private detailPanel: HTMLDivElement | undefined
    private loader?: LoaderComponent

    constructor(private newsService: NewsService = window.tbwServices.newsService) {
        super()
        this.className = 'tbw-news'
    }

    connectedCallback() {
        this.setNewsAttributes()
        this.init()
    }

    async init() {
        this.showLoader()
        let newsRes: NewsDO[] | undefined
        try {
            newsRes = await this.load()
        } finally {
            this.hideLoader()
        }
        if (!newsRes) { return }

        this.news = newsRes

        this.table = document.createElement('table')
        this.table.className = 'tbw-news-table'
        this.renderTable()

        const layout = document.createElement('div')
        layout.className = 'tbw-news-layout'
        const listWrapper = document.createElement('div')
        listWrapper.className = 'tbw-news-list'
        listWrapper.appendChild(this.table)
        layout.appendChild(listWrapper)
        this.createDetailPanel()
        this.appendChild(layout)
        this.attachClickTracking()
        this.i18n()
    }

    private createDetailPanel() {
        const panel = document.createElement('div')
        panel.className = 'tbw-news-detail-panel'
        panel.setAttribute('role', 'dialog')
        panel.setAttribute('aria-label', 'News article details')

        panel.innerHTML = `
            <div class="tbw-news-detail-sticky-div">
                <div class="tbw-news-detail-header">
                    <button type="button" class="tbw-news-detail-close" aria-label="Close">×</button>
                </div>
                <div class="tbw-news-detail-body">
                    <h2 class="tbw-news-detail-title"></h2>
                    <div class="tbw-news-detail-date"></div>
                    <p class="tbw-news-detail-summary"></p>
                    <div class="tbw-news-detail-source"></div>
                </div>
            </div>
        `

        const closeBtn = panel.querySelector('.tbw-news-detail-close')
        const close = () => this.closeDetailPanel()

        closeBtn?.addEventListener('click', close)

        this.detailPanel = panel
        document.body.appendChild(panel)
    }

    private openDetailPanel(news: NewsDO) {
        const dateLanguage = i18n.getLanguage() === 'en' ? DateOptionSet.dateEN : DateOptionSet.dateFR
        const publishedDate = formatDate(new Date(news.published), dateLanguage)

        const titleEl = this.detailPanel?.querySelector('.tbw-news-detail-title')
        const dateEl = this.detailPanel?.querySelector('.tbw-news-detail-date')
        const summaryEl = this.detailPanel?.querySelector('.tbw-news-detail-summary')
        const sourceEl = this.detailPanel?.querySelector('.tbw-news-detail-source')

        console.log(titleEl, summaryEl)

        if (titleEl) titleEl.textContent = news.title
        if (dateEl) {
            dateEl.innerHTML = `
                ${this.createTimeIcon().outerHTML}
                <span>${publishedDate}</span>
            `
        }
        if (summaryEl) summaryEl.textContent = news.summary ?? ''
        if (sourceEl) {
            const sourceName = news.source_name || this.getSourceName(news.link)
            sourceEl.innerHTML = `
                <a href="${news.link}" target="_blank" rel="noopener noreferrer">Continue on ${sourceName} →</a>
            `
        }

        this.detailPanel?.classList.add('is-open')
        this.detailPanel?.setAttribute('aria-hidden', 'false')
    }

    private closeDetailPanel() {
        this.detailPanel?.classList.remove('is-open')
        this.detailPanel?.setAttribute('aria-hidden', 'true')
    }

    disconnectedCallback() {
        if (this.detailPanel && this.detailPanel.parentNode === document.body) {
            document.body.removeChild(this.detailPanel)
        }
    }

    i18n() {
        const i18nElements = this.querySelectorAll<HTMLElement>('[i18n]')
        i18nElements.forEach((elem) => {
            const key = elem.getAttribute('i18n');
            if (key) { elem.innerText = i18n.t(key); }
        })
    }

    setNewsAttributes() {
        this.keywords = this.getAttribute('data-news-keywords') ?? null
        this.minScore = this.getAttribute('data-news-minScore') ?? null
    }

    async load() {
        return await this.newsService.load(this.keywords, this.minScore)
    }

    private renderTable() {
        if (!this.table) { return }
        this.table.innerHTML = ''
        this.createTopRow()
        this.createNewsTableBody()
    }

    async applyKeywordsFilter(keywords: string | null, minScore?: string | null) {
        const normalizedKeywords = keywords?.trim() || null
        const normalizedMinScore = minScore?.trim() || null

        this.keywords = normalizedKeywords
        this.minScore = normalizedMinScore

        if (this.keywords) {
            this.setAttribute('data-news-keywords', this.keywords)
        } else {
            this.removeAttribute('data-news-keywords')
        }

        if (this.minScore) {
            this.setAttribute('data-news-minScore', this.minScore)
        } else {
            this.removeAttribute('data-news-minScore')
        }

        this.showLoader()
        let newsRes: NewsDO[] | undefined
        try {
            newsRes = await this.load()
        } finally {
            this.hideLoader()
        }
        if (!newsRes) { return }

        this.news = newsRes
        this.renderTable()
        this.i18n()
    }

    private ensureLoader(): void {
        if (this.loader) { return }
        const el = document.createElement('div')
        el.className = 'tbw-loader'
        el.setAttribute('aria-hidden', 'true')
        this.appendChild(el)
        this.loader = new LoaderComponent(el)
        this.loader.hide()
    }

    private showLoader(): void {
        this.ensureLoader()
        this.classList.add('is-loading')
        this.setAttribute('aria-busy', 'true')
        this.loader?.show()
    }

    private hideLoader(): void {
        this.classList.remove('is-loading')
        this.removeAttribute('aria-busy')
        this.loader?.hide()
    }

    private createTopRow() {
        const titleLabel = i18n.t('news_title')
        const relevanceLabel = i18n.t('news_relevance')
        const relevanceTooltip = i18n.t('news_dapscore_tooltip')
        const publishedLabel = i18n.t('news_published')
        const topRow = document.createElement('thead')

        topRow.className = 'news-row top-row'
        topRow.innerHTML = `
            <tr>
                <th class="caption1">${titleLabel}</th>
                <th class="caption1">
                    <span class="news-header-with-tooltip">
                        ${relevanceLabel}
                        <span class="news-tooltip" aria-label="${relevanceTooltip}">
                            <span class="news-tooltip-icon">?</span>
                            <span class="news-tooltip-text">${relevanceTooltip}</span>
                        </span>
                    </span>
                </th>
                <th class="caption1">${publishedLabel}</th>
            </tr>
        `

        this.table?.appendChild(topRow)
    }

    private createTimeIcon(): SVGSVGElement {
        const timeIcon = document.createElementNS("http://www.w3.org/2000/svg", "svg")
        timeIcon.setAttribute("width", "12")
        timeIcon.setAttribute("height", "12")
        timeIcon.setAttribute("viewBox", "0 0 16 17")
        timeIcon.setAttribute("fill", "none")

        timeIcon.innerHTML = `
            <path d="M5.24023 2.19952V4.16474" stroke="var(--midnight-blue)" stroke-width="0.74211" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M10.4814 2.19952V4.16474" stroke="var(--midnight-blue)" stroke-width="0.74211" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M2.29297 6.84399H13.4292" stroke="var(--midnight-blue)" stroke-width="1.07194" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M13.7561 6.45749V12.0256C13.7561 13.9908 12.7735 15.301 10.4808 15.301H5.24021C2.94745 15.301 1.96484 13.9908 1.96484 12.0256V6.45749C1.96484 4.49227 2.94745 3.18213 5.24021 3.18213H10.4808C12.7735 3.18213 13.7561 4.49227 13.7561 6.45749Z" stroke="var(--midnight-blue)" stroke-width="1.07194" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
        `
        return timeIcon
    }

    getSourceName(link: string) {
        const extractFromURL = (url: string) => {
            try {
                const domain = new URL(url).hostname.replace('www.', '')
                return domain.split('.')[0]
            } catch {
                return url
            }
        }
        return capitalized(extractFromURL(link))
    }

    private createNewsTableBody() {
        const tableBody = document.createElement('tbody')
        tableBody.className = 'tbw-news-table-body'

        this.news.forEach((news, index) => {
            const newsRow = document.createElement('tr')
            newsRow.setAttribute('data-news-index', String(index))
            newsRow.classList.add('tbw-news-row-clickable')

            const sentiment: SentimentParams = SentimentParamsOptions[news.sentiment] ?? undefined
            const sentimentIcon = sentiment ? `<img src="${sentiment.icon}" alt="Sentiment Icon" class='news-row-icon'>` : `<div class='news-row-icon'></div>`
            const dateLanguage = i18n.getLanguage() === 'en' ? DateOptionSet.dateEN : DateOptionSet.dateFR
            const publishedDate = formatDate(new Date(news.published), dateLanguage)
            const sourceDomain = news.link.split('/')[2]
            const sourceIcon = `<div class ="source-icon">${news.source_name.slice(0, 1)}</div>`

            newsRow.innerHTML = `
                <td>
                    <a href="${news.link}" target="_blank" class='is-link' data-news-source="${sourceDomain}" data-news-index="${index}">
                        ${sentimentIcon}
                        <div class='news-row-content'>
                            ${news.source_logo ? `<img src="${news.source_logo}" alt="News Image" class='news-row-logo'>` : sourceIcon}
                            <p class="caption1">${news.title}</p>
                        </div>
                    </a>
                </td>
                <td>
                    <div class='caption1'>${news.DaPScore ?? ""}</div>
                </td>
                <td>
                    <div class='news-date-container'>${this.createTimeIcon().outerHTML}<p class="caption2">${publishedDate}</p></div>
                </td>
            `

            tableBody.appendChild(newsRow)
        })
        this.table?.appendChild(tableBody)
    }

    private attachClickTracking() {
        this.table?.addEventListener('click', (event) => {
            const target = event.target as HTMLElement
            const link = target.closest('a.is-link')
            if (!link) { return }
            const indexStr = link.getAttribute('data-news-index')
            if (indexStr != null) {
                event.preventDefault()
                const index = parseInt(indexStr, 10)
                const news = this.news[index]
                if (news) {
                    this.openDetailPanel(news)
                }
            }
            const newsSource = link.getAttribute("data-news-source") ?? null
            window.tbwServices?.analyticsService?.track?.('news_agg_click', { news_source: newsSource })
        })
    }
}
